/**
 * F-0407 SB-1 (`docs/concept-ownership-organisations-and-instances.md` §10.12): one consistent
 * indent step, replacing the namespace heading's `6 + depth * 14`, the work-area row's
 * `4 + depth * 14`, and the empty-namespace line's `26 + depth * 14` — three different base
 * offsets for the same step that, added to each row's own furniture (a namespace heading's icon,
 * a work area row's drag handle), read as "indent too high" (Jay, 2026-10-02, with a screenshot).
 *
 * `STEP` (12px) is the one new unit: a work area's name starts one step right of its own
 * namespace's name, and a subgroup indents one further step from its organisation — Jay's own
 * words for the target, not a number read off a screenshot. `BASE` (6px) is unchanged from the
 * namespace heading's own left inset, which this does not touch.
 */
export const WORK_AREA_INDENT_STEP = 12;
const BASE = 6;

/** A namespace heading's own `paddingLeft`, by its depth among organisations/subgroups (0 for a top-level one). */
export function namespaceHeadingIndent(depth: number): number {
  return BASE + depth * WORK_AREA_INDENT_STEP;
}

/**
 * A work area row's `paddingLeft`. `row.depth` is the work area's own nesting *within* its
 * namespace group (F-0380's `flattenWorkAreaHierarchy`); `group.depth` is that namespace's own
 * depth among organisations/subgroups. One step past its namespace heading's own indent, plus one
 * more per level the work area itself nests.
 */
export function workAreaRowIndent(groupDepth: number, rowDepth: number): number {
  return namespaceHeadingIndent(groupDepth) + (rowDepth + 1) * WORK_AREA_INDENT_STEP;
}

/** The "No work areas yet" line takes the same indent an actual work area row at that depth would. */
export function emptyNamespaceLineIndent(groupDepth: number): number {
  return workAreaRowIndent(groupDepth, 0);
}
